/**
 * useDepartments — shared hook for California department data
 * Source of truth: bu_discovery_results + v_california_departments (via /api/coverage/departments)
 * Only returns departments verified to have IT procurement POs on CaleProcure.
 *
 * Returns { departments, deptMap, loading }
 *   departments: [{ code, acronym, name, it_goods_count, it_services_count }] — sorted by acronym
 *   deptMap:      { [bu_code]: acronym } — for display lookups
 */
import { useState, useEffect } from 'react';
import apiClient from '../lib/apiClient';

// Module-level cache so multiple components share one fetch
let _cache = null;
let _promise = null;
const CACHE_TTL = 60 * 60 * 1000; // 1 hour
let _cacheTime = 0;

function fetchDepartments() {
  if (_promise && Date.now() - _cacheTime < CACHE_TTL) return _promise;
  // Stamp the window at REQUEST time, not at resolution. _cacheTime was only
  // set in the .then, so while the first request was in flight it was still
  // 0 — `Date.now() - 0 < CACHE_TTL` is false — and every component that
  // mounted before it came back started its OWN fetch and overwrote _promise.
  // A page with several history cards therefore fired one request per card,
  // which is exactly what the "module-level cache so multiple components
  // share one fetch" above says it does not do.
  _cacheTime = Date.now();
  _promise = apiClient.get('/api/coverage/departments')
    .then(data => {
      const list = (data?.departments || [])
        .filter(d => d.code && d.acronym)
        .sort((a, b) => a.acronym.localeCompare(b.acronym));
      const map = {};
      for (const d of list) {
        map[d.code] = d.acronym;
      }
      _cache = { departments: list, deptMap: map };
      _cacheTime = Date.now();
      return _cache;
    })
    .catch(err => {
      console.error('useDepartments: failed to load from backend:', err);
      _promise = null;
      return { departments: [], deptMap: {} };
    });
  return _promise;
}

export default function useDepartments() {
  const [state, setState] = useState(_cache || { departments: [], deptMap: {} });
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    if (_cache) {
      setState(_cache);
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetchDepartments().then(result => {
      if (!cancelled) {
        setState(result);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  return { ...state, loading };
}

/**
 * The bare fetch, for tests. The hook itself is the public surface; this
 * exists so the shared-request and failure behaviour can be asserted without
 * mounting four components to observe a module-level cache.
 */
export const fetchDepartmentsForTest = fetchDepartments;
